import { useMutation } from '@apollo/client';
import type { VariablesOf } from '@graphql-typed-document-node/core';
import React from 'react';
import type { SetNonNullable, SetRequired } from 'type-fest';
import useAppSelector, { useAppDispatch } from '../../app/redux/useAppSelector';
import { gql } from '../../graphql/generated/gql';
import type { User } from '../../graphql/generated/graphql';
import {
  TopicDetached,
  TopicMemberEditable,
  TopicPrivacy,
} from '../../graphql/generated/graphql';
import { UIActions } from '../../ui/uiSlice';
import useNavigate from '../../utils/router/useNavigate';
import topicPath from '../../utils/url/topicPath';
import TopicModal from './TopicModal';

const CREATE_TOPIC_MUTATION = gql(/** @complexity 2 */ `
  mutation createTopic(
    $name: String!
    $description: Json
    $memberEditable: TopicMemberEditable
    $detached: TopicDetached
    $parentId: ID
    $privacy: TopicPrivacy
    $inheritParent: Boolean
  ) {
    createTopic(
      name: $name
      description: $description
      memberEditable: $memberEditable
      detached: $detached
      parentId: $parentId
      privacy: $privacy
      inheritParent: $inheritParent
    ) {
      id
    }
  }
`);

const DEFAULT_MEMBER_EDITABLE = TopicMemberEditable.All;
const DEFAULT_PRIVACY = TopicPrivacy.Open;
const DEFAULT_DETACHED = TopicDetached.None;

type TopicVariables = VariablesOf<typeof CREATE_TOPIC_MUTATION>;
type NewTopic = SetNonNullable<
  SetRequired<TopicVariables, 'privacy'>,
  'privacy'
> & { id: string };

interface NewTopicModalProps {
  close: () => void;
  onCreate?: (newTopic: NewTopic) => void | Promise<void>;
  name?: string;
  parentId?: string;
}

const NewTopicModal = ({
  close,
  name,
  parentId,
  onCreate,
}: NewTopicModalProps) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [createTopic] = useMutation(CREATE_TOPIC_MUTATION);
  const user = useAppSelector((state) => state.session.user);

  const onSubmit = async (values: TopicVariables) => {
    const response = await createTopic({
      variables: {
        ...values,
        inheritParent:
          values.privacy !== TopicPrivacy.Private &&
          values.privacy !== TopicPrivacy.Secret,
        description: JSON.stringify(values.description),
      },
      update: (cache, { data }) => {
        cache.modify<User>({
          id: cache.identify(user),
          fields: {
            membershipTopics(existingRefs, { readField }) {
              if (
                !data ||
                existingRefs.some(
                  (ref) => readField('id', ref) === data.createTopic.id,
                )
              ) {
                return existingRefs;
              }
              return [
                ...existingRefs,
                cache.writeFragment({
                  data: data.createTopic,
                  fragment: gql(`
                fragment NewTopicModal_membershipTopics on Topic {
                  id
                }
              `),
                }),
              ];
            },
          },
        });
      },
    });
    const id = response.data?.createTopic?.id;

    if (id) {
      if (onCreate) {
        onCreate({ ...values, privacy: values.privacy ?? DEFAULT_PRIVACY, id });
      } else {
        navigate(topicPath({ id, name: values.name }), {
          state: { edit: true, new: true },
        });
      }
      dispatch(UIActions.closeModal({ reset: true }));
    }
  };

  return (
    <TopicModal
      close={close}
      name={name || ''}
      privacy={DEFAULT_PRIVACY}
      memberEditable={DEFAULT_MEMBER_EDITABLE}
      detached={DEFAULT_DETACHED}
      onSubmit={onSubmit}
      parentId={parentId}
      submitText="Create topic"
      title="Create a topic"
    />
  );
};

export default NewTopicModal;
